import { API_BASE_URL } from '../config';

// Helper function to ensure HTTPS URLs
const ensureHttps = (url) => {
    if (url.startsWith('http://')) {
        return url.replace('http://', 'https://');
    }
    return url;
};

// API responses captured when the page was prerendered at build time
// (scripts/build-sites.js), keyed by endpoint. They let the first render match
// the prerendered HTML instead of flashing a loading state; the hooks then
// fetch fresh data anyway, so changes in the backend still show up.
const PRELOADED = (typeof window !== 'undefined' && window.__PRELOADED_API__) || {};

// Helper function for API requests.
// Only set Content-Type on requests with a body — sending it on a plain GET
// turns the request into a CORS preflight which some backends/proxies reject.
const fetchAPI = async (endpoint, options = {}) => {
    const url = ensureHttps(`${API_BASE_URL}${endpoint}`);

    const hasBody = options.body !== undefined && options.body !== null;
    const headers = {
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers
    };

    const response = await fetch(url, {
        ...options,
        headers
    });

    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data;
};

const fetchFromURL = async (url, options = {}) => {
    url = ensureHttps(url);

    const hasBody = options.body !== undefined && options.body !== null;
    const headers = {
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers
    };

    const response = await fetch(url, {
        ...options,
        headers
    });

    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Also ensure any next/previous URLs in the response are HTTPS
    if (data.next) {
        data.next = ensureHttps(data.next);
    }
    if (data.previous) {
        data.previous = ensureHttps(data.previous);
    }

    return data;
}

// A facet field may arrive as a slug string ("web"), as an object
// ({ name, slug }) like tags do, or as a list of either.
const normalizeFacet = (raw) => {
    if (typeof raw === 'string') {
        const value = raw.trim().toLowerCase();
        return value ? { value, label: raw.trim() } : null;
    }
    if (raw && typeof raw === 'object') {
        const value = String(raw.slug || raw.value || raw.name || '').trim().toLowerCase();
        return value ? { value, label: raw.name || raw.slug || value } : null;
    }
    return null;
};

const facetEntries = (project, field) => {
    const raw = project?.[field];
    if (raw === undefined || raw === null) return [];
    const list = Array.isArray(raw) ? raw : [raw];
    return list.map(normalizeFacet).filter(Boolean);
};

// The demos the home page shows. Picked in the Django admin (`featured`),
// and the API returns them in the order set there (`featured_order`).
// Six fills whole rows at both two and three grid columns.
const FEATURED_FILTER = { featured: 'true' };

const withHttpsLinks = (data) => {
    if (data && typeof data === 'object') {
        if (data.next) data.next = ensureHttps(data.next);
        if (data.previous) data.previous = ensureHttps(data.previous);
    }
    return data;
};

// filters: { industry: 'it', category: 'all', ... } — 'all' and empty values
// are dropped, the rest are ANDed server-side.
const projectsEndpoint = (filters, projectType) => {
    const params = new URLSearchParams();
    for (const [field, value] of Object.entries(filters || {})) {
        if (value && value !== 'all') params.set(field, value);
    }
    if (projectType) params.set('project_type', projectType);
    const query = params.toString();
    return `/portfolio/projects/${query ? `?${query}` : ''}`;
};

// The whole group in one page — the source for the facets.
const groupEndpoint = (projectType) => {
    const params = new URLSearchParams({ page_size: '100' });
    if (projectType) params.set('project_type', projectType);
    return `/portfolio/projects/?${params}`;
};

const facetValues = (data, field) => {
    const counts = new Map();
    for (const project of data?.results || []) {
        for (const entry of facetEntries(project, field)) {
            const seen = counts.get(entry.value);
            counts.set(entry.value, {
                value: entry.value,
                label: seen?.label || entry.label,
                count: (seen?.count || 0) + 1
            });
        }
    }
    return [...counts.values()]
        .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
};

// Copies, so a later in-place fix-up can't alter the preloaded original.
const preloaded = (endpoint) =>
    PRELOADED[endpoint] ? JSON.parse(JSON.stringify(PRELOADED[endpoint])) : undefined;

// Data fetching functions
export const api = {
    // Synchronous lookups of the build-time data, for initial state only.
    // Each returns undefined when the page was not prerendered with it.
    peekProjects: (filters, projectType) => {
        const data = preloaded(projectsEndpoint(filters, projectType));
        return data && withHttpsLinks(data);
    },
    peekFeaturedProjects: (projectType) => {
        const data = preloaded(projectsEndpoint(FEATURED_FILTER, projectType));
        return data && withHttpsLinks(data);
    },
    peekGroupFacet: (projectType, field = 'category') => {
        const data = preloaded(groupEndpoint(projectType));
        return data && facetValues(data, field);
    },

    getProjects: async (filters = {}, projectType) => {
        try {
            return withHttpsLinks(await fetchAPI(projectsEndpoint(filters, projectType)));
        } catch (error) {
            console.error('Error fetching projects:', error);
            throw error;
        }
    },

    // Distinct values of one field across a project group, most projects first.
    // There is no endpoint listing them (/portfolio/project-categories/ 404s),
    // so they are derived from the projects themselves. A field the API does
    // not send yet simply yields [], and the caller renders no filter row.
    getGroupFacet: async (projectType, field = 'category') => {
        try {
            return facetValues(await fetchAPI(groupEndpoint(projectType)), field);
        } catch (error) {
            console.error(`Error fetching group facet "${field}":`, error);
            return [];
        }
    },

    // The hand-picked subset of a group, in the order set in the admin.
    getFeaturedProjects: async (projectType) => {
        try {
            return withHttpsLinks(await fetchAPI(projectsEndpoint(FEATURED_FILTER, projectType)));
        } catch (error) {
            console.error('Error fetching featured projects:', error);
            throw error;
        }
    },

    // Get projects from a specific URL (for pagination)
    getProjectFromUrl: async (url) => {
        try {
            // Handle relative URLs
            if (url.startsWith('/')) {
                url = `${API_BASE_URL}${url}`;
            }

            return await fetchFromURL(url);
        } catch (error) {
            console.error('Error fetching projects from URL:', error);
            throw error;
        }
    }
};

export default api;